/**
 * /2fa-verify — TOTP challenge during the login flow.
 *
 * Reached when the login (or activate) response had `totp_required:true`:
 * the access cookie was issued with totp=false, so any TOTP-gated route
 * (POSH ICC, complaints, offboarding /close, etc.) would 403 with
 * `totp_required`. POSTing /auth/totp/verify with the 6-digit
 * authenticator code makes the backend re-issue wr_access with the
 * totp=true claim; we then re-hydrate /me and continue to ?next= or
 * /dashboard.
 *
 * Passkeys (spec §6.2 B): when the person has a passkey (`methods` on the
 * pending /me) and this browser can use it, "Use passkey" leads and the
 * code form is the secondary "Use authenticator code instead". With no
 * passkey only the code form shows.
 *
 * Migration note: 1:1 port of
 * `frontend/src/app/(auth)/2fa-verify/page.tsx`.
 */

import { useEffect, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { KeyRound, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { z } from "zod";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { authApi, toApiError } from "@/lib/api";
import { useAuth, safeNext } from "@/lib/auth";
import { sessionLanding } from "@/lib/auth/auth-gate";
import {
  totpCodeSchema,
  type TotpCodeValues,
} from "@/lib/auth/schemas";
import { handleAuthError } from "@/lib/auth/form-errors";
import type { TwoFactorMethodFlags } from "@/types/auth";
import { twoFactorVerifyDescription } from "@/lib/auth/auth-copy";
import {
  isPasskeyCancelled,
  PASSKEY_UNUSABLE_MESSAGE,
  passkeyOnlyUnusable,
  passkeySupported,
  secondFactorChoice,
  verifyWithPasskey,
} from "@/lib/auth/passkey";

const twoFaVerifySearchSchema = z.object({
  next: z.string().optional().catch(undefined),
});

export const Route = createFileRoute("/_auth/2fa-verify")({
  validateSearch: twoFaVerifySearchSchema,
  component: TwoFactorVerifyPage,
});

function TwoFactorVerifyPage() {
  const navigate = useNavigate();
  const { next: nextRaw } = Route.useSearch();
  const { refresh, logout, user, loading } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const [supported, setSupported] = useState<boolean | null>(null);
  const [showCode, setShowCode] = useState(false);

  const nextPath = safeNext(nextRaw);

  useEffect(() => {
    let active = true;
    void passkeySupported().then((ok) => {
      if (active) setSupported(ok);
    });
    return () => {
      active = false;
    };
  }, []);

  // A session that is not pending (no `methods` on /me — e.g. reached from
  // a stale link after 2FA was done) reads its methods from the full-
  // session list instead, so a passkey-only person is never shown just the
  // code form. undefined = still loading; null = unknown → code form.
  const [listed, setListed] = useState<TwoFactorMethodFlags | null | undefined>(undefined);
  const pendingMethods = user?.methods;
  const signedIn = !loading && user !== null;
  useEffect(() => {
    if (!signedIn || pendingMethods) return;
    let active = true;
    authApi
      .getTwoFactorMethods({ skipStepUp: true })
      .then((m) => {
        if (active) setListed({ passkey: m.passkeys.length > 0, totp: m.totp_enabled });
      })
      .catch(() => {
        if (active) setListed(null);
      });
    return () => {
      active = false;
    };
  }, [signedIn, pendingMethods]);

  // The pending /me says which second factors can finish the sign-in
  // (the login response carried the same flags; login() hydrated /me).
  const methods = pendingMethods ?? listed;
  const choice = secondFactorChoice(methods, supported === true);
  const deciding =
    loading || supported === null || (signedIn && !pendingMethods && listed === undefined);
  // A passkey is the only method and this browser can't use one: no code
  // form — only the device that holds the passkey can finish.
  const unusable = passkeyOnlyUnusable(methods, supported === true);
  const codeVisible = !unusable && (choice.primary === "totp" || showCode);

  // No session at all (a bookmark, a stale tab after sign-out): the form
  // could only fail — go to sign-in, keeping where they were headed.
  useEffect(() => {
    if (!loading && user === null) {
      void navigate({ to: "/login", search: { next: nextPath }, replace: true });
    }
  }, [loading, user, navigate, nextPath]);

  async function continueSignedIn(joined?: string[]) {
    // The emailed confirm link was used, and this two-step check finished
    // joining the company (or companies) that were waiting for it.
    if (joined && joined.length > 0) {
      toast.success(`You've joined ${joined.join(", ")}.`, {
        description: "Switch to it from the company menu.",
      });
    }
    const me = await refresh();
    // The next unfinished step (temporary password, workspace pick) or
    // the destination.
    const route = sessionLanding(me, nextPath);
    void navigate({ to: route.to, search: route.search as never, replace: true });
  }

  async function onPasskey() {
    setPasskeyBusy(true);
    try {
      const res = await verifyWithPasskey();
      if (isPasskeyCancelled(res)) return;
      await continueSignedIn(res?.joined_companies);
    } catch (err) {
      toast.error(toApiError(err).message || "That passkey could not be verified. Try again.");
    } finally {
      setPasskeyBusy(false);
    }
  }

  const form = useForm<TotpCodeValues>({
    resolver: zodResolver(totpCodeSchema),
    defaultValues: { code: "" },
  });

  async function onSubmit(values: TotpCodeValues) {
    setSubmitting(true);
    try {
      // Login already issued wr_access (totp=false) + wr_csrf. This
      // call re-issues wr_access with totp=true so TOTP-gated routes
      // pass.
      const res = await authApi.totpVerify({ code: values.code });
      await continueSignedIn(res?.joined_companies);
    } catch (err) {
      handleAuthError(err, form.setError, ["code"]);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="mb-1 flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-accent text-accent-foreground">
            <ShieldCheck className="h-4 w-4" />
          </span>
          <CardTitle className="text-2xl">Two-Factor Verification</CardTitle>
        </div>
        <CardDescription>
          {twoFactorVerifyDescription(codeVisible ? "totp" : "passkey")}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {deciding && (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        )}
        {!deciding && choice.primary === "passkey" && (
          <div className="space-y-3">
            <Button
              type="button"
              className="w-full"
              onClick={onPasskey}
              disabled={passkeyBusy || submitting}
              autoFocus
              data-testid="use-passkey"
            >
              {passkeyBusy ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <KeyRound className="h-4 w-4" />
              )}
              Use Passkey
            </Button>
            {choice.codeFallback && !showCode && (
              <p className="text-center text-sm">
                <button
                  type="button"
                  onClick={() => setShowCode(true)}
                  className="inline-flex items-center font-medium text-primary hover:underline [@media(pointer:coarse)]:min-h-11"
                  data-testid="use-code-instead"
                >
                  Use Authenticator Code Instead
                </button>
              </p>
            )}
          </div>
        )}
        {!deciding && unusable && (
          <p
            role="alert"
            className="mb-4 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive"
            data-testid="passkey-unusable"
          >
            {PASSKEY_UNUSABLE_MESSAGE}
          </p>
        )}
        {!deciding && codeVisible && (
        <div className={choice.primary === "passkey" ? "mt-4 border-t border-border pt-4" : undefined}>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4"
            noValidate
          >
            <FormField
              control={form.control}
              name="code"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="sr-only">
                    Authenticator Code
                  </FormLabel>
                  <FormControl>
                    <Input
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      placeholder="123456"
                      maxLength={6}
                      autoFocus
                      className="text-center text-lg tracking-[0.5em]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button
              type="submit"
              className="w-full"
              disabled={submitting}
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              Verify and Continue
            </Button>
          </form>
        </Form>
        </div>
        )}
        <p className="mt-4 text-center text-sm text-muted-foreground">
          <button
            type="button"
            onClick={async () => {
              await logout();
              void navigate({ to: "/login", replace: true });
            }}
            className="inline-flex items-center font-medium text-primary hover:underline [@media(pointer:coarse)]:min-h-11"
          >
            Sign In as a Different User
          </button>
        </p>
      </CardContent>
    </Card>
  );
}
